import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Check } from "lucide-react";

export default function CoachApplyPage() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", years_coaching: 2, specialties: "", current_clients: 5, about: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault(); setBusy(true); setErr("");
        try {
            await api.post("/growth/coach-application", form);
            setDone(true);
        } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[var(--bg)]" data-testid="coach-apply-page">
            <div className="max-w-3xl mx-auto px-6 py-14">
                <Link to="/"><Logo size="sm" /></Link>
                <p className="label-tiny mt-14 mb-3">/ For serious coaches only</p>
                <h1 className="font-serif text-6xl mb-5">Apply for<br /><span className="gold-gradient-text italic">coach access.</span></h1>
                <p className="text-[var(--text-muted)] max-w-2xl text-[15px] leading-relaxed mb-10">
                    NDE Fitness is invite-only while we onboard our first 1,000 coaches. Tell us about your practice — selected applicants get founding-coach pricing, priority AI credits, and direct onboarding with the team.
                </p>
                <div className="gold-rule mb-10" />

                {done ? (
                    <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-8" data-testid="apply-success">
                        <Check size={32} className="text-[var(--gold-bright)] mb-4" />
                        <h2 className="font-serif text-3xl mb-3">Application received.</h2>
                        <p className="text-sm text-[var(--text-muted)]">We review every application personally. Expect a response within 3 business days at <span className="font-mono text-[var(--text)]">{form.email}</span>.</p>
                        <Link to="/" className="btn-ghost mt-6 inline-block">← Back home</Link>
                    </div>
                ) : (
                    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="apply-form">
                        <div>
                            <label className="label-tiny block mb-2">Full name</label>
                            <input required className="input-dark" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-name" />
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Email</label>
                            <input required type="email" className="input-dark" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apply-email" />
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Phone (optional)</label>
                            <input className="input-dark" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Years coaching</label>
                            <input type="number" min={0} className="input-dark" value={form.years_coaching} onChange={(e) => setForm({ ...form, years_coaching: parseInt(e.target.value) || 0 })} />
                        </div>
                        <div className="md:col-span-2">
                            <label className="label-tiny block mb-2">Specialties</label>
                            <input className="input-dark" placeholder="e.g. hypertrophy, powerlifting, post-rehab, hybrid athlete" value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-tiny block mb-2">Current clients</label>
                            <input type="number" min={0} className="input-dark" value={form.current_clients} onChange={(e) => setForm({ ...form, current_clients: parseInt(e.target.value) || 0 })} />
                        </div>
                        <div className="md:col-span-2">
                            <label className="label-tiny block mb-2">Tell us about your practice</label>
                            <textarea className="input-dark min-h-[120px]" value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} placeholder="Who do you coach, what's your edge, what do you need?" />
                        </div>
                        {err && <div className="md:col-span-2 text-sm text-[var(--danger)]">{err}</div>}
                        <div className="md:col-span-2 flex justify-between items-center pt-3">
                            <Link to="/login" className="text-sm text-[var(--text-muted)] hover:text-[var(--gold)]">← Cancel</Link>
                            <button type="submit" disabled={busy} className="btn-primary" data-testid="apply-submit">
                                {busy ? "Sending…" : "Submit application →"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
